import { z } from 'zod';
import type { HitProp } from '../../components/algolia-search/types';
import type { ProductAdvisorResponse } from './ProductAdvisor.types';

const featuredImageSchema = z.object({
  src: z.string(),
});

const galleryImageSchema = z.preprocess(
  (value) => (typeof value === 'object' && value !== null ? value : {}),
  z.object({}).loose(),
);

const cheapestPriceConfigSchema = z.object({
  cheapestItem: z.record(z.string(), z.unknown()),
}).loose();

const productImagesSchema = z.preprocess(
  (value) => (typeof value === 'object' && value !== null ? value : {}),
  z.object({
    featuredImage: featuredImageSchema.optional(),
    firstImageFromGallery: galleryImageSchema.optional(),
  }).loose(),
);

const productAdvisorHitInputSchema = z.object({
  objectID: z.string(),
  path: z.string(),
  image: featuredImageSchema.optional(),
  productImages: productImagesSchema,
  featuredDescription: z.unknown().optional(),
  cheapestPriceConfig: z.unknown().optional(),
}).loose();

const productAdvisorHitSchema = productAdvisorHitInputSchema.transform((hit): HitProp | null => {
  const image = hit.image ?? hit.productImages?.featuredImage;

  if (!image) {
    return null;
  }

  const firstImageFromGallery = hit.productImages?.firstImageFromGallery as
    HitProp['productImages']['firstImageFromGallery'] | undefined;

  const parsedHit: HitProp = {
    ...hit,
    image,
    productImages: {
      featuredImage: hit.productImages?.featuredImage ?? image,
      firstImageFromGallery: firstImageFromGallery ?? {},
    },
  } as HitProp;

  if (typeof hit.featuredDescription !== 'string') {
    delete parsedHit.featuredDescription;
  }

  const cheapestPriceConfig = cheapestPriceConfigSchema.safeParse(hit.cheapestPriceConfig);

  if (!cheapestPriceConfig.success) {
    delete parsedHit.cheapestPriceConfig;
  }

  return parsedHit;
});

const productAdvisorMessageSchema = z.object({
  role: z.literal('assistant'),
  type: z.literal('products').optional(),
  content: z.string(),
  metadata: z.array(productAdvisorHitSchema)
    .transform((metadata) => metadata.filter((hit): hit is HitProp => Boolean(hit)))
    .optional(),
});

const productAdvisorResponseSchema = z.object({
  messages: z.array(productAdvisorMessageSchema).min(1),
});

export const parseProductAdvisorResponse = (body: unknown): ProductAdvisorResponse | null => {
  const parsedResponse = productAdvisorResponseSchema.safeParse(body);

  return parsedResponse.success ? parsedResponse.data : null;
};
